import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ReconciliationTransactionDataTableDocument } from 'generated/resources/datatable.graphql'
import { NewReconciliationSidePanelDocument } from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AccountReconciliationExportModal } from 'modules/banking/components/AccountReconciliationExportModal'
import { ReconcileTransaction } from 'modules/banking/components/ReconcileTransaction'
import { ReconciliationsTransactionsDataTable } from 'modules/banking/components/ReconciliationsTransactionsDataTable'
import { useSnoozeTransaction } from 'modules/banking/hooks/useSnoozeTransaction'
import { InvoiceDetailPanel } from 'modules/finance/components/InvoiceDetailPanel'

const NewReconciliationSidePanel = () => {
  const { id: bankAccountId } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [invoiceId, setInvoiceId] = useState<string | undefined>()
  const [associatedExpensableId, setAssociatedExpensableId] = useState<string | undefined>(
    undefined
  )
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)
  const { data } = useQuery(NewReconciliationSidePanelDocument)
  const { reconciliationsSeeFinancialDocumentsPage } = data?.banking?._policies || {
    reconciliationsSeeFinancialDocumentsPage: { can: false },
  }
  const { snooze, unsnooze } = useSnoozeTransaction()
  const apolloClient = useApolloClient()
  const [showExportReconciliationModal, setShowExportReconciliationModal] = useState<boolean>(false)

  const snoozeTransaction = useUnsafeCallback(
    async (transactionId: string, until: Date) => {
      const transaction = await snooze(transactionId, until)
      if (transaction) {
        apolloClient.refetchQueries({
          include: [ReconciliationTransactionDataTableDocument],
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.reconciliations.submit_messages.transaction_snoozed'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.reconciliations.submit_messages.transaction_snoozed_error'),
        })
      }
    },
    [snooze, apolloClient]
  )

  const unsnoozeTransaction = useUnsafeCallback(
    async (transactionId: string) => {
      const transaction = await unsnooze(transactionId)
      if (transaction) {
        apolloClient.refetchQueries({
          include: [ReconciliationTransactionDataTableDocument],
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.reconciliations.submit_messages.transaction_unsnoozed'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.reconciliations.submit_messages.transaction_unsnoozed_error'),
        })
      }
    },
    [unsnooze, apolloClient]
  )

  if (!bankAccountId || !data) return null

  const onCloseSidePanel = () =>
    navigate(navigation.finance.banking.accounts.accountId.fullPath({ id: bankAccountId }))

  const canManageExpenses = data?.expenses?._policies?.expenseManageExpenses?.can ?? false

  if (!reconciliationsSeeFinancialDocumentsPage.can)
    return (
      <Sidepanel
        title={i18n.t('banking.accounts.reconciliations')}
        width="extended"
        open
        onClose={onCloseSidePanel}
      >
        <NotFound />
      </Sidepanel>
    )

  if (canManageExpenses && associatedExpensableId) {
    navigate(
      navigation.expenses.list.expenses.expenseId.fullPath({
        expense_id: associatedExpensableId,
      })
    )
    setInvoiceId(undefined)
    setAssociatedExpensableId(undefined)
  }

  return (
    <>
      <Sidepanel
        title={i18n.t('banking.accounts.reconciliations')}
        width="extended"
        open
        onClose={onCloseSidePanel}
      >
        {invoiceId !== undefined && associatedExpensableId === undefined && (
          <InvoiceDetailPanel invoiceId={invoiceId} onClose={() => setInvoiceId(undefined)} />
        )}
        {showExportReconciliationModal && (
          <AccountReconciliationExportModal
            onClose={() => setShowExportReconciliationModal(false)}
            bankAccountId={String(bankAccountId)}
          />
        )}
        <Box
          flexDirection="row"
          style={{
            marginTop: '-24px',
            marginBottom: '-24px',
            marginRight: '-24px',
            height: 'calc(100vh - 65px)',
            width: 'calc(100% + 24px)',
          }}
        >
          <Box
            gap="s24"
            padding="s16"
            paddingLeft="s0"
            overflowY="auto"
            border={{ right: { color: 'grey400', width: 's1', style: 'solid' } }}
            style={{ scrollbarGutter: 'auto', width: '50%' }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Box gap="s16">
                <Text.Normal weight="semibold">
                  {i18n.t('banking.reconciliations.transactions_list.title')}
                </Text.Normal>
                <Text.Normal weight="normal">
                  {i18n.t('banking.reconciliations.transactions_list.description')}
                </Text.Normal>
              </Box>
              <Button
                onClick={() => setShowExportReconciliationModal(true)}
                type="primary"
                iconName="download"
              />
            </Box>

            <ReconciliationsTransactionsDataTable
              selectedTransactionId={selectedTransactionId}
              onSelect={setSelectedTransactionId}
              onSnooze={snoozeTransaction}
              onUnsnooze={unsnoozeTransaction}
              bankAccountId={bankAccountId}
            />
          </Box>
          <Box style={{ width: '50%' }} gap="s16" paddingTop="s16">
            <ReconcileTransaction
              transactionId={selectedTransactionId}
              onTransactionFullyReconciled={() => setSelectedTransactionId(null)}
              onInvoiceDetailClick={(id, expensableId) => {
                setInvoiceId(id)
                setAssociatedExpensableId(expensableId)
              }}
            />
          </Box>
        </Box>
      </Sidepanel>
    </>
  )
}

export default NewReconciliationSidePanel
